<template>
  <div
    class="modal"
    :class="{ 'is-active': value }">

    <div 
      class="modal-background"
      @click="close()"
    >
    </div>
    
    <div class="modal-content">
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">{{ modalTitle }}</p>
          <button
            class="delete"
            aria-label="close"
            @click="close(true)">
          </button>
        </header>

        <v-form class="modal-card-body" :valid.sync="valid">
          <div class="field">
            <label class="label">Name*</label>
            <v-text-field 
              required
              placeholder="Enter Installation Name"
              v-model="installation.installation_name">
            </v-text-field>
          </div>

          <div class="field">
            <label class="label">Installation Type*</label>
            <v-select
              required
              :options="installationTypes"
              item-text="installation_type_name"
              item-value="id"
              v-model="installation.installation_type_id"
            />
          </div>

          <div class="columns">
            <div class="column">
              <label class="label">Unit Type*</label>
              <v-select
                required
                :options="units"
                item-text="unit_display_name"
                item-value="id"
                v-model="installation.unit_id"
              />
            </div>

            <div class="column">
              <label class="label">Unit Price*</label>
              <v-text-field
                required
                type="number"
                placeholder="Installation Unit Price"
                v-model="installation.unit_price"/>
            </div>
          </div>

          <div class="field">
            <label class="label">Waste %*</label>
            <v-text-field
              required
              type="number"
              v-model="installation.waste_pct"/>
          </div>

          <div class="field">
            <label class="label">Default Summary Text</label>
            <v-rich-text
              v-model="installation.description"
            />
          </div>

          <div class="field">
            <label class="label">Default Descriptive List</label>
            <v-rich-text
              v-model="installation.desc_list"
            />
          </div>

          <div class="field">
            <label class="label">Intallation Image*</label>
            <v-drop-zone 
              id='install-method-image'
              :value="imageUpload.file"
              @input="imageUpload.file = $event; changedImage = true"
              :file-name.sync="imageUpload.name"
              required
              height="110px"
            />
          </div>
        </v-form>

        <footer class="modal-card-foot">
          <button 
            :disabled="!valid || saving"
            :class="{ 'is-loading': saving }"
            class="button is-primary" 
            @click.stop="add()">
            Save
          </button>
          <button 
            class="button" 
            @click.stop="close(true)">
            Cancel
          </button>
        </footer>

      </div>
    </div>
  </div>
</template>

<script>
import VDropZone from '@/components/base/VDropZone'
import VTextField from '@/components/base/VTextField'
import VSelect from '@/components/base/VSelect'
import VForm from '@/components/base/VForm'
import VRichText from '@/components/base/VRichText'

import { mapState, mapActions, mapMutations } from 'vuex'

export default {
  props: {
    editingInstallation: {
      type: Object
    },
    value: {
      type: Boolean
    }
  },
  components: { VTextField, VForm, VDropZone, VSelect, VRichText },
  data() {
    return {
      installation: {
        installation_name: '',
        installation_type_id: null,
        unit_id: null,
        unit_price: null,
        waste_pct: null,
        description: null,
        desc_list: null
      },
      valid: false,
      imageUpload: {
        file: null,
        name: null
      },
      changedImage: false,
      saving: false
    }
  },
  computed: {
    ...mapState("installations", ["installationTypes"]),
    ...mapState("units", ["units"]),
    modalTitle({ editingInstallation }) {
      return editingInstallation
        ? 'Edit Installation'
        : 'Add an Installation'
    }
  },
  watch: {
    value(v) {
      if (v && this.editingInstallation) {
        this.installation = this.editingInstallation
        this.imageUpload.file = this.editingInstallation.image
        this.imageUpload.name = this.editingInstallation.filename
      } else {
        this.clearData()
      }
    }
  },
  methods: {
    ...mapActions("installations", [
      "getInstallationTypes", "addInstallation", "addImage", "updateInstallation"
    ]),
    ...mapActions("units", ["getUnits"]),
    ...mapMutations("app", ["setNotification"]),
    initialize() {
      if (this.units.length == 0) {
        this.getUnits();
      }
      if (this.installationTypes.length == 0) {
        this.getInstallationTypes();
      }
    },
    updateImage(id) {
      let formData = new FormData();
      formData.append('image', this.imageUpload.file, this.imageUpload.file.name);

      this.addImage({ 
        installationId: id, data: formData 
      }).then(() => {
        this.saving = false;
        this.notifySuccess();
      });
    },
    add() {
      this.saving = true;
      if (!this.editingInstallation) {
        this.addInstallation(this.installation).then(resp => {
          if (this.imageUpload.file) {
            this.updateImage(resp.data.id);
          } else {
            this.saving = false;
            this.notifySuccess();
          }
        });
      } else {
        let saveFields = [
          'installation_name', 'installation_type_id', 'unit_id', 
          'unit_price', 'waste_pct', 'description', 'desc_list'
        ]

        let payload = {}
        saveFields.forEach(field => payload[field] = this.installation[field]);

        this.updateInstallation({
          installationId: this.installation.id,
          data: payload
        }).then(() => {
          if (this.imageUpload.file && this.changedImage) {
            this.updateImage(this.installation.id);
          } else {
            this.saving = false;
            this.notifySuccess();
          }
        })
      }
    },
    notifySuccess() {
      this.setNotification({ 
        type: 'success',
        status: true,
        message: 'Installation successfully saved'
      });
      this.close(true);
    },
    close(clear=false) {
      this.$emit('input', false);
      if (clear) {
        this.clearData();
      }
    },
    clearData() {
      Object.assign(this.$data, this.$options.data.call(this));
    }
  }
}
</script>

<style scoped>
.modal-card {
  border-radius: 6px;
}
</style>
